import { z } from 'zod';

import { IsoDateTimeSchema } from '../common/datetime';
import { DeviceId } from '../common/ids';

import { StaffMeSchema } from './me';
import { PrivacyNoticeVersionSchema } from './privacy';

// ---------------------------------------------------------------------------
// Token de invitación (data-model.md · worker_invitations; enlace `/activar/{token}`)
// ---------------------------------------------------------------------------

/** Token en claro (32 bytes base64url). Solo viaja en la URL de activación y en `redeem-code`. */
export const InvitationTokenSchema = z.string().min(1).max(512);

/** Parámetro de ruta `{token}` de `/activation/{token}/*`. */
export const ActivationTokenParamsSchema = z.object({ token: InvitationTokenSchema });
export type ActivationTokenParams = z.infer<typeof ActivationTokenParamsSchema>;

// ---------------------------------------------------------------------------
// `GET /activation/{token}`
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Verificación del documento (005 · QA-6)
// ---------------------------------------------------------------------------

/**
 * Intentos fallidos admitidos por invitación. Al llegar a este número la invitación pasa a
 * `REVOKED` con motivo `VERIFICATION_FAILED` y el administrador debe emitir otra.
 */
export const DOCUMENT_VERIFICATION_MAX_ATTEMPTS = 5;

/** Caracteres que se comparan: los últimos 4 alfanuméricos del documento (o todo si tiene menos). */
export const DOCUMENT_LAST_CHARS = 4;

/**
 * Lo que escribe la persona: hasta 4 caracteres alfanuméricos; se admiten espacios y guiones, que
 * el servidor descarta antes de comparar (mayúsculas y minúsculas son equivalentes).
 */
export const DocumentLast4Schema = z
  .string()
  .trim()
  .min(1, { message: 'Ingresa los últimos 4 dígitos de tu documento' })
  .max(12)
  .regex(/^[0-9A-Za-z -]+$/, { message: 'Solo letras y números' })
  .refine((value) => value.replace(/[^0-9A-Za-z]/g, '').length <= DOCUMENT_LAST_CHARS, {
    message: 'Ingresa solo los últimos 4 dígitos de tu documento',
  });

/** `POST /activation/{token}/verify-document`: `{ document_last4 }`. */
export const VerifyDocumentBodySchema = z.object({ document_last4: DocumentLast4Schema });
export type VerifyDocumentBody = z.infer<typeof VerifyDocumentBodySchema>;

/** Estado de la verificación de la invitación (sin datos del trabajador). */
export const DocumentVerificationStateSchema = z.object({
  /** `true` si alguien ya verificó el documento con este enlace (`document_verified_at`). */
  verified: z.boolean(),
  attempts_remaining: z.number().int().min(0).max(DOCUMENT_VERIFICATION_MAX_ATTEMPTS),
});
export type DocumentVerificationState = z.infer<typeof DocumentVerificationStateSchema>;

/**
 * `GET /activation/{token}` y `200` de `POST /activation/{token}/verify-document`.
 *
 * QA-6: **ningún dato del trabajador antes de verificar**. `GET` responde siempre
 * `worker_first_name: null`; el nombre de pila solo viaja en la respuesta de una verificación
 * correcta (quien la hizo demostró conocer el documento). Nunca expone documento, teléfono ni
 * email (FR-006).
 */
export const ActivationInfoSchema = z.object({
  worker_first_name: z.string().nullable(),
  company_trade_name: z.string(),
  privacy_notice: z.object({
    version: PrivacyNoticeVersionSchema,
    accepted: z.boolean(),
  }),
  expires_at: IsoDateTimeSchema,
  document_verification: DocumentVerificationStateSchema,
});
export type ActivationInfo = z.infer<typeof ActivationInfoSchema>;

// ---------------------------------------------------------------------------
// Código de traspaso iOS (research R-5)
// ---------------------------------------------------------------------------

/** Alfabeto sin caracteres ambiguos (sin 0, O, 1, I): 32 símbolos. */
export const HANDOFF_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const HANDOFF_CODE_LENGTH = 6;
export const HANDOFF_CODE_TTL_MINUTES = 15;
export const HANDOFF_CODE_PATTERN = new RegExp(
  `^[${HANDOFF_CODE_ALPHABET}]{${HANDOFF_CODE_LENGTH}}$`,
);

/** Código de traspaso tal como lo emite el servidor (`ABC234`). */
export const HandoffCodeSchema = z.string().regex(HANDOFF_CODE_PATTERN, {
  message: 'El código debe tener 6 caracteres (letras y dígitos, sin 0, O, 1 ni I)',
});
export type HandoffCode = z.infer<typeof HandoffCodeSchema>;

/** `201 { code, expires_at }` de `POST /activation/{token}/handoff-code`. */
export const HandoffCodeResponseSchema = z.object({
  code: HandoffCodeSchema,
  expires_at: IsoDateTimeSchema,
});
export type HandoffCodeResponse = z.infer<typeof HandoffCodeResponseSchema>;

/** `POST /activation/redeem-code`: `{ code }` (se normaliza a mayúsculas y sin espacios). */
export const RedeemCodeBodySchema = z.object({
  code: z.string().trim().toUpperCase().regex(HANDOFF_CODE_PATTERN, {
    message: 'El código debe tener 6 caracteres (letras y dígitos, sin 0, O, 1 ni I)',
  }),
});
export type RedeemCodeBody = z.infer<typeof RedeemCodeBodySchema>;

/** `200 { token }`: el token de invitación (aún `PENDING`) para continuar en la PWA instalada. */
export const RedeemCodeResponseSchema = z.object({
  token: InvitationTokenSchema,
});
export type RedeemCodeResponse = z.infer<typeof RedeemCodeResponseSchema>;

// ---------------------------------------------------------------------------
// `POST /activation/{token}/enroll`
// ---------------------------------------------------------------------------

/** `201 { device_id, me }` + Set-Cookie `fermin_staff_session`. Body: `DeviceEnrollBodySchema`. */
export const EnrollResponseSchema = z.object({
  device_id: DeviceId,
  me: StaffMeSchema,
});
export type EnrollResponse = z.infer<typeof EnrollResponseSchema>;
